import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { FiChevronLeft, FiChevronRight, FiSearch } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { useDebounce } from '@/hooks/useDebounce'
import { getUser } from '@/utils/auth'
import { ROUTES } from '@/constants/routes'
import SalesforceSyncButton from '@/components/common/SalesforceSyncButton'
import PageSizeSelect from '@/components/common/PageSizeSelect'
import PdfViewerModal from '@/pages/finance/PdfViewerModal'
import {
  getSalesforceOrders,
  getSalesforceOrderXeroInvoice,
  getSalesforceOrderXeroInvoicePdfUrl,
  SalesforceOrder,
  updateSalesforceOrderOutcome,
} from '@/services/api/salesforce'
import {
  startSalesforceSync,
  subscribeSalesforceSync,
  watchSalesforceSyncInBackground,
  type SalesforceSyncState,
} from '@/services/salesforceSync'
import { getDocumentFileUrl } from '@/services/api/stockDispatch'

const money = (value: string | number | null | undefined, currency = 'ZAR') => {
  if (value == null || value === '') return '—'
  const amount = Number(value)
  if (!Number.isFinite(amount)) return String(value)
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: currency || 'ZAR',
    maximumFractionDigits: 2,
  }).format(amount)
}

const date = (value: string | null | undefined) =>
  value
    ? new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—'

const qcLabel = (order: SalesforceOrder) => {
  if (order.orderFulfillment?.outcome === 'Accepted') return 'QC accepted'
  if (order.orderFulfillment?.outcome === 'Rejected') return 'QC rejected'
  return 'Awaiting QC'
}

const orderLot = (order: SalesforceOrder) => order.lot || order.orderFulfillment?.lotBatch || ''

const qcStyle = (order: SalesforceOrder) => {
  if (order.orderFulfillment?.outcome === 'Accepted') {
    return { color: '#137A43', background: '#E8F7EF' }
  }
  if (order.orderFulfillment?.outcome === 'Rejected') {
    return { color: '#B42318', background: '#FDEBE9' }
  }
  return { color: '#8A6100', background: '#FFF7D6' }
}

const OrderFulfillment = () => {
  const role = getUser()?.role
  const canManage = role === 'admin' || role === 'operations_manager'
  const [orders, setOrders] = useState<SalesforceOrder[]>([])
  const [page, setPage] = useState(1)
  const [resultsPerPage, setResultsPerPage] = useState(25)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [search, setSearch] = useState('')
  const query = useDebounce(search, 350)
  const [outcome, setOutcome] = useState<'' | 'Pending' | 'Accepted' | 'Rejected'>('')
  const [loading, setLoading] = useState(true)
  const [syncJob, setSyncJob] = useState<SalesforceSyncState>({
    status: 'idle',
    fetched: 0,
    processed: 0,
    upserted: 0,
    since: '2026-04-01',
    percent: 0,
  })
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [invoicePdfUrl, setInvoicePdfUrl] = useState<string | null>(null)
  const [invoiceOpenUrl, setInvoiceOpenUrl] = useState<string | null>(null)
  const [invoiceTitle, setInvoiceTitle] = useState('KCG invoice')
  const [invoiceViewerOpen, setInvoiceViewerOpen] = useState(false)
  const [invoiceLoading, setInvoiceLoading] = useState(false)
  const [invoiceError, setInvoiceError] = useState<string | null>(null)
  const [noteViewerOpen, setNoteViewerOpen] = useState(false)
  const [notePdfUrl, setNotePdfUrl] = useState<string | null>(null)
  const [noteTitle, setNoteTitle] = useState('Warehouse dispatch note')
  const [noteLoading, setNoteLoading] = useState(false)
  const [noteError, setNoteError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getSalesforceOrders({
        page,
        limit: resultsPerPage,
        search: query || undefined,
        outcome: outcome || undefined,
      })
      setOrders(result.orders)
      setTotal(result.total)
      setTotalPages(Math.max(result.totalPages, 1))
    } catch (error: any) {
      toast.error(error?.message || 'Could not load Salesforce Orders.')
    } finally {
      setLoading(false)
    }
  }, [outcome, page, query, resultsPerPage])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    setPage(1)
  }, [query, outcome])

  useEffect(() => {
    watchSalesforceSyncInBackground()
    return subscribeSalesforceSync((job) => {
      setSyncJob(job)
      if (job.status === 'completed') {
        void load()
      }
    })
  }, [load])

  const handleSync = async () => {
    if (syncJob.status === 'running') return
    try {
      await startSalesforceSync()
    } catch (error: any) {
      toast.error(error?.message || 'Could not start Salesforce Order sync.')
    }
  }

  const closeInvoiceViewer = () => {
    setInvoiceViewerOpen(false)
    setInvoiceLoading(false)
    setInvoiceError(null)
    setInvoiceOpenUrl(null)
    if (invoicePdfUrl) URL.revokeObjectURL(invoicePdfUrl)
    setInvoicePdfUrl(null)
  }

  const openXeroInvoice = async (order: SalesforceOrder) => {
    if (!order.xeroInvoiceId) {
      toast.error('This order has no KCG invoice number.')
      return
    }
    if (invoicePdfUrl) URL.revokeObjectURL(invoicePdfUrl)
    setInvoicePdfUrl(null)
    setInvoiceError(null)
    setInvoiceOpenUrl(null)
    setInvoiceTitle(`Invoice ${order.xeroInvoiceId}`)
    setInvoiceViewerOpen(true)
    setInvoiceLoading(true)
    setUpdatingId(order.id)
    try {
      const invoice = await getSalesforceOrderXeroInvoice(order.id)
      setInvoiceTitle(`Invoice ${invoice.invoiceNumber}`)
      setInvoiceOpenUrl(invoice.openUrl)
      if (invoice.lots.length > 0) {
        toast.success(`Lots from ${invoice.invoiceNumber}: ${invoice.lots.join(', ')}`)
        await load()
      } else {
        toast.info(`${invoice.invoiceNumber} has no invoice batches on the line items.`)
      }
      const blobUrl = await getSalesforceOrderXeroInvoicePdfUrl(order.id)
      setInvoicePdfUrl(blobUrl)
    } catch (error: any) {
      const message = error?.message || 'Could not load this invoice from Xero.'
      setInvoiceError(message)
      toast.error(message)
    } finally {
      setInvoiceLoading(false)
      setUpdatingId(null)
    }
  }

  const closeNoteViewer = () => {
    setNoteViewerOpen(false)
    setNoteLoading(false)
    setNoteError(null)
    setNotePdfUrl(null)
  }

  const openDispatchNote = async (dispatch: {
    id: string
    warehouse?: { name: string } | null
  }) => {
    setNoteTitle(dispatch.warehouse?.name ? `Dispatch note · ${dispatch.warehouse.name}` : 'Warehouse dispatch note')
    setNoteError(null)
    setNotePdfUrl(null)
    setNoteViewerOpen(true)
    setNoteLoading(true)
    try {
      const url = await getDocumentFileUrl(dispatch.id, 'dispatchNote', 'primary')
      if (!url) {
        setNoteError('This trip has no warehouse dispatch note file.')
        return
      }
      setNotePdfUrl(url)
    } catch (error: any) {
      const message = error?.message || 'Could not open the warehouse dispatch note.'
      setNoteError(message)
      toast.error(message)
    } finally {
      setNoteLoading(false)
    }
  }

  const handleOutcome = async (order: SalesforceOrder, next: string) => {
    const value = next === 'Rejected' ? 'Rejected' : null
    setUpdatingId(order.id)
    try {
      await updateSalesforceOrderOutcome(order.id, value)
      toast.success(
        value === 'Rejected'
          ? 'QC rejected — fulfillment remains open.'
          : 'QC rejection cleared. Fully paid will be accepted automatically.'
      )
      await load()
    } catch (error: any) {
      toast.error(error?.message || 'Could not update QC outcome.')
    } finally {
      setUpdatingId(null)
    }
  }

  return (
    <div className="p-6" style={{ fontFamily: 'Lexend Deca' }}>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="mb-1 text-xs" style={{ color: '#8B8B98' }}>
            Operations / Order Fulfillment
          </p>
          <h1 className="text-2xl font-semibold" style={{ color: '#212126' }}>
            Order Fulfillment
          </h1>
          <p className="mt-2 max-w-3xl text-sm" style={{ color: '#69697A' }}>
            Open the KCG invoice from Xero on this page. Invoice batches come from
            that invoice. Fully paid becomes QC accepted. The last column is the
            warehouse dispatch note (Zacpak PDF).
          </p>
        </div>
        {canManage && (
          <SalesforceSyncButton
            job={syncJob}
            onClick={handleSync}
            idleLabel="Refresh from Salesforce"
          />
        )}
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border bg-white p-4" style={{ borderColor: '#E9E9EA' }}>
          <p className="text-xs" style={{ color: '#69697A' }}>Matching orders</p>
          <p className="mt-1 text-2xl font-semibold" style={{ color: '#212126' }}>{total}</p>
        </div>
        <div className="rounded-lg border bg-white p-4" style={{ borderColor: '#E9E9EA' }}>
          <p className="text-xs" style={{ color: '#69697A' }}>Commercial source</p>
          <p className="mt-1 text-sm font-semibold" style={{ color: '#00A1E0' }}>Salesforce</p>
        </div>
        <div className="rounded-lg border bg-white p-4" style={{ borderColor: '#E9E9EA' }}>
          <p className="text-xs" style={{ color: '#69697A' }}>Completion rule</p>
          <p className="mt-1 text-sm font-semibold" style={{ color: '#137A43' }}>Fully paid → QC accepted</p>
        </div>
      </div>

      <div className="rounded-lg border bg-white" style={{ borderColor: '#E9E9EA' }}>
        <div className="flex flex-wrap gap-3 border-b p-4" style={{ borderColor: '#E9E9EA' }}>
          <label className="relative min-w-[260px] flex-1">
            <FiSearch className="absolute left-3 top-3" style={{ color: '#8B8B98' }} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search order, KCG invoice, customer or product"
              className="h-10 w-full rounded-md border pl-9 pr-3 text-sm outline-none"
              style={{ borderColor: '#D9D9DE' }}
            />
          </label>
          <select
            value={outcome}
            onChange={(event) => setOutcome(event.target.value as typeof outcome)}
            className="h-10 rounded-md border bg-white px-3 text-sm"
            style={{ borderColor: '#D9D9DE', color: '#212126' }}
          >
            <option value="">All QC states</option>
            <option value="Pending">Awaiting QC</option>
            <option value="Accepted">QC accepted</option>
            <option value="Rejected">QC rejected</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead style={{ background: '#F7F8F7', color: '#69697A' }}>
              <tr>
                <th className="px-4 py-3 font-medium">Salesforce Order</th>
                <th className="px-4 py-3 font-medium">KCG invoice</th>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium">Invoice batches</th>
                <th className="px-4 py-3 font-medium">Order date</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Payment</th>
                <th className="px-4 py-3 font-medium">Salesforce</th>
                <th className="px-4 py-3 font-medium">Fulfillment / QC</th>
                <th className="px-4 py-3 font-medium">Warehouse dispatch note</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center" style={{ color: '#69697A' }}>Loading orders…</td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center" style={{ color: '#69697A' }}>No Salesforce Orders match this view.</td></tr>
              ) : (
                orders.map((order) => {
                  const dispatches =
                    order.warehouseDispatches?.length
                      ? order.warehouseDispatches
                      : order.orderFulfillment?.stockDispatches || []
                  const qc = qcStyle(order)
                  return (
                    <tr key={order.id} className="border-t" style={{ borderColor: '#EFEFF1' }}>
                      <td className="whitespace-nowrap px-4 py-3 font-medium" style={{ color: '#212126' }}>
                        {order.orderNumber || '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        {order.xeroInvoiceId ? (
                          <button
                            type="button"
                            disabled={updatingId === order.id}
                            onClick={() => void openXeroInvoice(order)}
                            className="text-left font-medium underline-offset-2 hover:underline disabled:opacity-50"
                            style={{ color: '#00A1E0' }}
                          >
                            {order.xeroInvoiceId}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="min-w-[220px] px-4 py-3">{order.accountName || '—'}</td>
                      <td className="min-w-[240px] px-4 py-3">
                        <span className="text-xs" style={{ color: orderLot(order) ? '#212126' : '#69697A' }}>
                          {orderLot(order) || '—'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">{date(order.effectiveDate)}</td>
                      <td className="whitespace-nowrap px-4 py-3">{money(order.invoiceAmount, order.currencyIsoCode || 'ZAR')}</td>
                      <td className="whitespace-nowrap px-4 py-3">{order.paymentStatus || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="rounded-full px-2 py-1 text-xs" style={{ color: '#555563', background: '#F2F4F2' }}>
                          {order.status || 'Draft'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {order.orderFulfillment?.outcome === 'Accepted' || !canManage ? (
                          <span className="rounded-full px-2 py-1 text-xs font-medium" style={qc}>{qcLabel(order)}</span>
                        ) : (
                          <select
                            value={order.orderFulfillment?.outcome || ''}
                            disabled={updatingId === order.id}
                            onChange={(event) => handleOutcome(order, event.target.value)}
                            className="rounded-md border px-2 py-1.5 text-xs font-medium disabled:opacity-50"
                            style={{ borderColor: '#D9D9DE', ...qc }}
                          >
                            <option value="">Awaiting QC</option>
                            <option value="Rejected">QC rejected</option>
                          </select>
                        )}
                      </td>
                      <td className="min-w-[200px] px-4 py-3 text-xs" style={{ color: '#69697A' }}>
                        {dispatches.length === 0 ? (
                          'No warehouse dispatch'
                        ) : (
                          <div className="flex flex-col gap-1">
                            {dispatches.slice(0, 3).map((dispatch) => (
                              <div key={dispatch.id} className="flex flex-wrap items-center gap-2">
                                <Link
                                  to={ROUTES.LOAD_OUT_DETAIL.replace(':id', dispatch.id)}
                                  className="font-medium"
                                  style={{ color: '#00A1E0' }}
                                >
                                  {dispatch.warehouse?.name || 'Stock Dispatch'}
                                </Link>
                                {dispatch.dispatchNoteImageUrl ||
                                ('hasDispatchNote' in dispatch && dispatch.hasDispatchNote) ? (
                                  <button
                                    type="button"
                                    onClick={() => void openDispatchNote(dispatch)}
                                    className="underline-offset-2 hover:underline"
                                    style={{ color: '#137A43' }}
                                  >
                                    View note
                                  </button>
                                ) : null}
                              </div>
                            ))}
                            {dispatches.length > 3 ? (
                              <span>+{dispatches.length - 3} more</span>
                            ) : null}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t px-4 py-3" style={{ borderColor: '#E9E9EA' }}>
          <span className="text-xs" style={{ color: '#69697A' }}>
            Page {page} of {totalPages} · {total} orders
          </span>
          <div className="flex items-center gap-2">
            <PageSizeSelect
              value={resultsPerPage}
              onChange={(size) => {
                setResultsPerPage(size)
                setPage(1)
              }}
            />
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((value) => value - 1)}
              className="rounded-md border p-2 disabled:opacity-40"
              style={{ borderColor: '#D9D9DE' }}
              aria-label="Previous page"
            >
              <FiChevronLeft />
            </button>
            <button
              type="button"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((value) => value + 1)}
              className="rounded-md border p-2 disabled:opacity-40"
              style={{ borderColor: '#D9D9DE' }}
              aria-label="Next page"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      <PdfViewerModal
        isOpen={invoiceViewerOpen}
        onClose={closeInvoiceViewer}
        pdfUrl={invoicePdfUrl}
        title={invoiceTitle}
        isLoading={invoiceLoading}
        openInXeroUrl={invoiceOpenUrl}
        errorMessage={invoiceError}
      />
      <PdfViewerModal
        isOpen={noteViewerOpen}
        onClose={closeNoteViewer}
        pdfUrl={notePdfUrl}
        title={noteTitle}
        isLoading={noteLoading}
        errorMessage={noteError}
      />
    </div>
  )
}

export default OrderFulfillment
